import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import { Page } from 'src/components/Page';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IHistoryGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { IHistory } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { historyApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { HistoryFormDialog } from './HistoryFormDialog';

/**
 * ## History
 * 회사 연혁 관리 페이지
 */

export const History = () => {
  const { getHistoryGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IHistoryGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getHistoryData = useCallback(async () => {
    const pageParams = {
      page: pagination.page,
      size: pagination.size,
    };
    await historyApi.getHistory(pageParams).then((res) => {
      if (res) {
        const newGridRows = res.data.map((history, i) => {
          return { ...history, id: history.historySid ?? i };
        });
        setGridRows(newGridRows as IHistoryGridRow[]);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [pagination.page, pagination.size]);

  useEffect(() => {
    getHistoryData();
  }, [getHistoryData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.HISTORY,
      formType: FormType.CREATE,
      dialogContent: (
        <HistoryFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IHistoryGridRow>) => {
    callApiWithLoading(async () => {
      await historyApi.getDetail(param.row.historySid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.HISTORY,
            formType: FormType.UPDATE,
            dialogContent: (
              <HistoryFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data as IHistory}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.HISTORY);
    getHistoryData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPagination((prev) => ({
      ...prev,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    }));
  };

  return (
    <Page>
      <ContentBox>
        <ContentBoxButtons>
          <ContentButtonCreate onClick={handleAdd}>
            {t('common.button.register')}
          </ContentButtonCreate>
        </ContentBoxButtons>
        <CDataGridPro
          columns={getHistoryGridCol()}
          rows={gridRows}
          rowCount={toJS(pagination.totalElements)}
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.size,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          onRowDoubleClick={handleRowDoubleClick}
          slots={{ footer: CGridFooter }}
        />
      </ContentBox>
    </Page>
  );
};
export default observer(History);
